import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AdminAccountRole, AdminPermissions, Facilitator, GameSession } from '@business-game/shared';
import { EMPTY_OPERATOR_PERMISSIONS, FACILITATOR_PERMISSIONS } from '@business-game/shared';
import { Check, ChevronRight, Copy, Link2, LogIn, Pencil, Search, UserPlus, X } from 'lucide-react';
import { AdminUserPermissionsEditor } from '../../components/admin/AdminUserPermissionsEditor';
import { authApi, type AdminAuthUser, type AdminUserStatus } from '../../services/api/auth.api';
import { facilitatorsApi } from '../../services/api/facilitators.api';
import { sessionsApi } from '../../services/api/sessions.api';
import { getApiErrorMessage, normalizeAdminEmail, normalizeAdminLogin } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Constantes & helpers (niveau module : aucun composant n'est défini dans un render)
// ---------------------------------------------------------------------------

const MIN_PASSWORD_LENGTH = 8;

const ROLE_OPTIONS: Array<{ value: AdminAccountRole; label: string; description: string }> = [
  { value: 'admin', label: 'Administrateur', description: 'Tous les droits.' },
  { value: 'super_admin', label: 'Super admin', description: 'Tous les droits, y compris sur tous les comptes.' },
  { value: 'facilitator', label: 'Intervenant', description: 'Anime ses sessions.' },
  { value: 'operator', label: 'Opérateur', description: 'Droits personnalisés.' },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label])) as Record<AdminAccountRole, string>;

const STATUS_STYLES: Record<AdminUserStatus, { label: string; className: string }> = {
  active: { label: 'Actif', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  pending: { label: 'Invitation en attente', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  disabled: { label: 'Désactivé', className: 'bg-slate-100 text-slate-500 ring-slate-200' },
};

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/10';
const labelClass = 'mb-1 ml-0.5 block text-xs font-bold uppercase tracking-wider text-slate-400';
const primaryButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white transition hover:bg-violet-600 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
const rowActionClass =
  'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-500 transition hover:bg-violet-50 hover:text-violet-700 disabled:opacity-50';

const buildSetupLink = (token: string) =>
  `${window.location.origin}/admin/set-password?token=${encodeURIComponent(token)}`;

const getStatus = (user: AdminAuthUser): AdminUserStatus => user.status ?? 'active';

/** « Marie Dupont » → { first: 'Marie', last: 'Dupont' } (découpe sur le premier espace). */
const splitName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return { first: first ?? '', last: rest.join(' ') };
};

/**
 * Les intervenants ne voient que les sessions dont le facilitateur porte exactement `facilitatorName` :
 * on crée (ou réactive — l'API est idempotente) l'entité facilitateur et on reprend son nom exact.
 */
const ensureFacilitator = async (name: string): Promise<string> => {
  try {
    return (await facilitatorsApi.createFacilitator({ name })).name;
  } catch {
    // ponytail: droits insuffisants sur /facilitators → on garde le nom saisi, l'entité pourra être créée depuis une session.
    return name;
  }
};

const getAssignableRoles = (currentUser: AdminAuthUser): AdminAccountRole[] => [
  'operator',
  'facilitator',
  ...(currentUser.canManageAllUsers ? (['admin'] as const) : []),
  ...(currentUser.isSuperAdmin ? (['super_admin'] as const) : []),
];

/** Permissions par défaut quand on change de rôle (même logique que l'ancien sélecteur). */
const defaultPermissionsFor = (role: AdminAccountRole, current: AdminPermissions): AdminPermissions =>
  role === 'facilitator' ? FACILITATOR_PERMISSIONS : role === 'operator' ? EMPTY_OPERATOR_PERMISSIONS : current;

type RightsState = {
  role: AdminAccountRole;
  permissions: AdminPermissions;
  facilitatorName: string;
  assignedSessionIds: number[];
};

const rightsPayload = (rights: RightsState) => ({
  role: rights.role,
  permissions: rights.role === 'operator' ? rights.permissions : undefined,
  facilitatorName: rights.role === 'facilitator' ? rights.facilitatorName : null,
  assignedSessionIds: rights.role === 'facilitator' ? rights.assignedSessionIds : undefined,
});

// ---------------------------------------------------------------------------
// Petits composants
// ---------------------------------------------------------------------------

const Avatar: React.FC<{ user: AdminAuthUser }> = ({ user }) => {
  const dim = 'h-9 w-9 text-sm';
  if (user.logoUrl) {
    return <img src={user.logoUrl} alt="" className={`${dim} shrink-0 rounded-full border border-slate-200 bg-white object-contain`} />;
  }
  const initials = (user.displayName || user.email || '?')
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className={`${dim} flex shrink-0 items-center justify-center rounded-full bg-violet-100 font-black text-violet-700`}>
      {initials}
    </span>
  );
};

const StatusBadge: React.FC<{ status: AdminUserStatus }> = ({ status }) => (
  <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${STATUS_STYLES[status].className}`}>
    {STATUS_STYLES[status].label}
  </span>
);

const ErrorBox: React.FC<{ message: string }> = ({ message }) =>
  message ? (
    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{message}</div>
  ) : null;

const Modal: React.FC<{ title: React.ReactNode; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <div className="relative w-full max-w-2xl overflow-hidden rounded-[20px] bg-white shadow-2xl shadow-violet-500/15 ring-1 ring-slate-200/80">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 border-l-4 border-l-violet-500 px-6 py-4">
              <div className="min-w-0 text-lg font-black text-slate-900">{title}</div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto px-6 py-5">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Nom du manager, avec son login si un autre compte porte le même nom. */
const managerLabel = (users: AdminAuthUser[], id: number): string => {
  const manager = users.find((u) => u.id === id);
  if (!manager) return `#${id}`;
  const homonym = users.some((u) => u.id !== id && u.displayName === manager.displayName);
  return homonym ? `${manager.displayName} (${manager.login})` : manager.displayName;
};

type MailLang = 'fr' | 'en';

/** Mail d'invitation prêt à coller (pas de système d'envoi d'email). */
const buildInviteMail = (lang: MailLang, { name, login, link }: { name: string; login: string; link: string }): string => {
  const firstName = name.trim().split(/\s+/)[0] || name;
  const loginUrl = `${window.location.origin}/admin/login`;
  if (lang === 'en') {
    return [
      'Subject: Your TeamLab access',
      '',
      `Hello ${firstName},`,
      '',
      'An account has been created for you on TeamLab.',
      '',
      '1. Click this link to choose your password (valid for 7 days, single use):',
      link,
      '',
      `2. Then sign in at any time at ${loginUrl} with:`,
      `   - Username: ${login}`,
      '   - the password you chose.',
      '',
      'If the link has expired, just reply to this email and we will send you a new one.',
      '',
      'Best regards,',
    ].join('\n');
  }
  return [
    'Objet : Votre accès à TeamLab',
    '',
    `Bonjour ${firstName},`,
    '',
    'Un compte a été créé pour vous sur TeamLab.',
    '',
    '1. Cliquez sur ce lien pour choisir votre mot de passe (valable 7 jours, utilisable une seule fois) :',
    link,
    '',
    `2. Ensuite, connectez-vous à tout moment sur ${loginUrl} avec :`,
    `   - Identifiant : ${login}`,
    '   - le mot de passe que vous aurez choisi.',
    '',
    'Si le lien a expiré, répondez simplement à ce mail pour en recevoir un nouveau.',
    '',
    'Bonne journée,',
  ].join('\n');
};

/** Lien d'accès copiable (invitation, réinitialisation, nouveau lien) + mail prêt à coller FR/EN. */
const LinkShare: React.FC<{ link: string; name: string; login: string }> = ({ link, name, login }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<'link' | MailLang | null>(null);
  const [manualText, setManualText] = useState('');

  const copy = async (what: 'link' | MailLang) => {
    const text = what === 'link' ? link : buildInviteMail(what, { name, login, link });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setManualText('');
    } catch {
      // Contexte non sécurisé ou permission refusée : on affiche le texte pour un Ctrl+C manuel.
      if (what === 'link') inputRef.current?.select();
      setManualText(what === 'link' ? '' : text);
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        readOnly
        value={link}
        onFocus={(e) => e.target.select()}
        className={`${inputClass} font-mono text-xs text-slate-600`}
      />
      <button type="button" onClick={() => void copy('link')} className={`${primaryButtonClass} w-full py-3`}>
        {copied === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied === 'link' ? 'Lien copié' : 'Copier le lien'}
      </button>
      <div className="grid grid-cols-2 gap-2">
        {(['fr', 'en'] as const).map((lang) => (
          <button key={lang} type="button" onClick={() => void copy(lang)} className={`${secondaryButtonClass} w-full`}>
            {copied === lang ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied === lang ? 'Mail copié' : lang === 'fr' ? 'Copier le mail (FR)' : 'Copy email (EN)'}
          </button>
        ))}
      </div>
      {manualText && (
        <textarea readOnly value={manualText} rows={8} onFocus={(e) => e.target.select()} className={`${inputClass} font-mono text-xs`} />
      )}
      <p className="text-center text-xs font-medium text-slate-400">Valable 7 jours, à usage unique.</p>
    </div>
  );
};

const RolePicker: React.FC<{
  value: AdminAccountRole;
  allowed: AdminAccountRole[];
  onChange: (role: AdminAccountRole) => void;
}> = ({ value, allowed, onChange }) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
    {ROLE_OPTIONS.filter((option) => allowed.includes(option.value) || option.value === value).map((option) => {
      const selected = option.value === value;
      const disabled = !allowed.includes(option.value);
      return (
        <label
          key={option.value}
          className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition ${
            selected ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-200'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          <input
            type="radio"
            name="role"
            checked={selected}
            disabled={disabled}
            onChange={() => onChange(option.value)}
            className="mt-0.5 h-4 w-4 accent-violet-600"
          />
          <span className="min-w-0">
            <span className="block text-sm font-bold text-slate-900">{option.label}</span>
            <span className="block text-xs text-slate-500">{option.description}</span>
          </span>
        </label>
      );
    })}
  </div>
);

/** Rôle + droits détaillés repliables (opérateur / facilitateur). */
const RightsFields: React.FC<{
  rights: RightsState;
  onChange: (rights: RightsState) => void;
  allowedRoles: AdminAccountRole[];
  sessions: GameSession[];
  facilitators: Facilitator[];
  /** Nom utilisé si « Nouveau facilitateur » est choisi. */
  newFacilitatorName: string;
}> = ({ rights, onChange, allowedRoles, sessions, facilitators, newFacilitatorName }) => {
  const facilitatorNames = facilitators.map((f) => f.name);
  if (rights.facilitatorName && !facilitatorNames.includes(rights.facilitatorName)) {
    facilitatorNames.push(rights.facilitatorName);
  }

  const summary =
    rights.role === 'facilitator'
      ? `${rights.assignedSessionIds.length} session(s) assignée(s)`
      : `${Object.values(rights.permissions.sections).filter(Boolean).length} section(s) visible(s)`;

  return (
    <div className="space-y-3">
      <RolePicker
        value={rights.role}
        allowed={allowedRoles}
        onChange={(role) => onChange({ ...rights, role, permissions: defaultPermissionsFor(role, rights.permissions) })}
      />
      {rights.role === 'facilitator' && (
        <div>
          <label className={labelClass}>Facilitateur lié</label>
          <select
            value={rights.facilitatorName}
            onChange={(e) => onChange({ ...rights, facilitatorName: e.target.value })}
            className={inputClass}
          >
            <option value="">Nouveau facilitateur{newFacilitatorName ? ` : ${newFacilitatorName}` : ''}</option>
            {facilitatorNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
      {(rights.role === 'operator' || rights.role === 'facilitator') && (
        <details className="group rounded-xl border border-slate-200 bg-slate-50/60">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-bold text-slate-700">
            <ChevronRight className="h-4 w-4 text-slate-400 transition group-open:rotate-90" />
            Personnaliser les droits
            <span className="ml-auto text-xs font-medium text-slate-400">{summary}</span>
          </summary>
          <div className="border-t border-slate-200 p-3">
            <AdminUserPermissionsEditor
              role={rights.role}
              permissions={rights.permissions}
              facilitatorName={rights.facilitatorName}
              assignedSessionIds={rights.assignedSessionIds}
              sessions={sessions}
              canManageRoles={false}
              hideFacilitatorName
              onRoleChange={(role) => onChange({ ...rights, role })}
              onPermissionsChange={(permissions) => onChange({ ...rights, permissions })}
              onFacilitatorNameChange={(facilitatorName) => onChange({ ...rights, facilitatorName })}
              onAssignedSessionIdsChange={(assignedSessionIds) => onChange({ ...rights, assignedSessionIds })}
            />
          </div>
        </details>
      )}
    </div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-violet-500">{children}</h3>
);

// ---------------------------------------------------------------------------
// Modale d'invitation
// ---------------------------------------------------------------------------

const InviteModal: React.FC<{
  currentUser: AdminAuthUser;
  existingUsers: AdminAuthUser[];
  sessions: GameSession[];
  facilitators: Facilitator[];
  onClose: () => void;
  onCreated: (user: AdminAuthUser) => void;
}> = ({ currentUser, existingUsers, sessions, facilitators, onClose, onCreated }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [rights, setRights] = useState<RightsState>({
    role: 'facilitator',
    permissions: FACILITATOR_PERMISSIONS,
    facilitatorName: '',
    assignedSessionIds: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ name: string; login: string; link?: string } | null>(null);

  const defaultFacilitatorName = `${firstName.trim()} ${lastName.trim().toUpperCase()}`.trim();

  const handleRightsChange = (next: RightsState) => {
    // Choisir un facilitateur existant pré-remplit Prénom / Nom s'ils sont vides.
    if (next.facilitatorName && next.facilitatorName !== rights.facilitatorName) {
      const { first, last } = splitName(next.facilitatorName);
      if (!firstName.trim()) setFirstName(first);
      if (!lastName.trim()) setLastName(last);
    }
    setRights(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const normalizedEmail = normalizeAdminEmail(email);
    if (normalizedEmail && existingUsers.some((user) => user.email === normalizedEmail || user.login === normalizedEmail)) {
      setError('Cet email est déjà utilisé par un compte existant.');
      return;
    }
    setSubmitting(true);
    try {
      const facilitatorName =
        rights.role === 'facilitator' ? await ensureFacilitator(rights.facilitatorName || defaultFacilitatorName) : '';
      const { user, setupToken } = await authApi.createUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: normalizedEmail || undefined,
        ...rightsPayload({ ...rights, facilitatorName }),
      });
      onCreated(user);
      setResult({ name: firstName.trim(), login: user.login, link: setupToken ? buildSetupLink(setupToken) : undefined });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erreur lors de la création du compte'));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Modal title={`Compte créé pour ${result.name}`} onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Envoyez ce lien : il permet de choisir son mot de passe. Identifiant de connexion :{' '}
            <span className="font-mono font-bold text-slate-900">{result.login}</span>
          </p>
          {result.link ? <LinkShare link={result.link} name={result.name} login={result.login} /> : <ErrorBox message="Aucun lien reçu du serveur." />}
          <button type="button" onClick={onClose} className={`${secondaryButtonClass} w-full`}>
            Terminé
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Inviter un utilisateur" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Prénom</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} required autoFocus />
          </div>
          <div>
            <label className={labelClass}>Nom</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} required />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Email (facultatif)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </div>
        </div>

        <div>
          <label className={labelClass}>Rôle</label>
          <RightsFields
            rights={rights}
            onChange={handleRightsChange}
            allowedRoles={getAssignableRoles(currentUser)}
            sessions={sessions}
            facilitators={facilitators}
            newFacilitatorName={defaultFacilitatorName}
          />
        </div>

        <ErrorBox message={error} />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className={`${secondaryButtonClass} flex-1`}>
            Annuler
          </button>
          <button type="submit" disabled={submitting} className={`${primaryButtonClass} flex-1`}>
            {submitting ? 'Création…' : 'Créer et obtenir le lien'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Modale d'édition
// ---------------------------------------------------------------------------

const EditModal: React.FC<{
  user: AdminAuthUser;
  currentUser: AdminAuthUser;
  users: AdminAuthUser[];
  sessions: GameSession[];
  facilitators: Facilitator[];
  onClose: () => void;
  onUpdated: (user: AdminAuthUser) => void;
}> = ({ user, currentUser, users, sessions, facilitators, onClose, onUpdated }) => {
  const isSelf = user.id === currentUser.id;
  const status = getStatus(user);

  const [displayName, setDisplayName] = useState(user.displayName);
  const hasEmail = user.hasEmail !== false;
  const [email, setEmail] = useState(hasEmail ? user.email : '');
  const [login, setLogin] = useState(user.login);
  const [logoUrl, setLogoUrl] = useState(user.logoUrl || '');
  const canChooseManager = currentUser.isSuperAdmin && !isSelf;
  const [managerId, setManagerId] = useState<string>(user.parentAdminUserId ? String(user.parentAdminUserId) : '');
  const [rights, setRights] = useState<RightsState>({
    role: user.role,
    permissions: user.permissions,
    facilitatorName: user.facilitatorName || '',
    assignedSessionIds: user.assignedSessionIds || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Accès & sécurité
  const [panel, setPanel] = useState<'none' | 'confirmReset' | 'password'>('none');
  const [resetLink, setResetLink] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [securityBusy, setSecurityBusy] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [securityNotice, setSecurityNotice] = useState('');

  const runSecurity = async (action: () => Promise<void>, fallback: string) => {
    setSecurityError('');
    setSecurityNotice('');
    setSecurityBusy(true);
    try {
      await action();
    } catch (err) {
      setSecurityError(getApiErrorMessage(err, fallback));
    } finally {
      setSecurityBusy(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const facilitatorName =
        rights.role === 'facilitator' ? await ensureFacilitator(rights.facilitatorName || displayName.trim()) : '';
      const updated = await authApi.updateUser(user.id, {
        displayName: displayName.trim(),
        // Email vide (compte sans email) → on n'envoie rien pour garder le placeholder backend.
        email: normalizeAdminEmail(email) || undefined,
        login: normalizeAdminLogin(login),
        logoUrl: logoUrl.trim() || null,
        ...rightsPayload({ ...rights, facilitatorName }),
        ...(canChooseManager ? { parentAdminUserId: managerId ? Number(managerId) : null } : {}),
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erreur lors de la mise à jour de l’utilisateur'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () =>
    runSecurity(async () => {
      const { user: updated, setupToken } = await authApi.resetLink(user.id);
      onUpdated(updated);
      setResetLink(buildSetupLink(setupToken));
      setPanel('none');
    }, 'Impossible de réinitialiser le mot de passe');

  const handleSetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setSecurityError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    if (password !== passwordConfirmation) {
      setSecurityError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    void runSecurity(async () => {
      onUpdated(await authApi.updateUser(user.id, { password }));
      setPassword('');
      setPasswordConfirmation('');
      setPanel('none');
      setSecurityNotice('Mot de passe enregistré.');
    }, 'Impossible d’enregistrer le mot de passe');
  };

  const handleToggleActive = () =>
    runSecurity(async () => {
      onUpdated(await authApi.updateUser(user.id, { isActive: status === 'disabled' }));
    }, 'Impossible de modifier le statut du compte');

  const handleLoginAs = () =>
    runSecurity(async () => {
      const result = await authApi.loginAs(user.id);
      localStorage.setItem('teamlab_admin_token', result.token);
      window.location.href = '/admin';
    }, 'Impossible de se connecter en tant que cet utilisateur');

  return (
    <Modal
      title={
        <span className="flex items-center gap-3">
          <Avatar user={user} />
          <span className="truncate">{user.displayName}</span>
          <StatusBadge status={status} />
        </span>
      }
      onClose={onClose}
    >
      <div className="space-y-6">
        <form onSubmit={handleSave} className="space-y-6">
          <section>
            <SectionTitle>Identité</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Nom affiché</label>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Email{hasEmail ? '' : ' (à compléter)'}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required={hasEmail} />
              </div>
              <div>
                <label className={labelClass}>Login</label>
                <input value={login} onChange={(e) => setLogin(e.target.value)} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>URL du logo</label>
                <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="/logos/mon-logo.png" className={inputClass} />
              </div>
            </div>
          </section>

          <section>
            <SectionTitle>Rôle & droits</SectionTitle>
            <RightsFields
              rights={rights}
              onChange={setRights}
              allowedRoles={getAssignableRoles(currentUser)}
              sessions={sessions}
              facilitators={facilitators}
              newFacilitatorName={displayName.trim()}
            />
            {canChooseManager && (
              <div className="mt-4">
                <label className={labelClass}>Géré par</label>
                <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className={inputClass}>
                  <option value="">Personne (super admins uniquement)</option>
                  {users
                    .filter((u) => u.id !== user.id && u.role !== 'facilitator')
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName} ({u.login})
                      </option>
                    ))}
                </select>
              </div>
            )}
          </section>

          <ErrorBox message={error} />

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className={`${secondaryButtonClass} flex-1`}>
              Annuler
            </button>
            <button type="submit" disabled={saving} className={`${primaryButtonClass} flex-1`}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>

        <section className="border-t border-slate-100 pt-5">
          <SectionTitle>Accès & sécurité</SectionTitle>

          {resetLink ? (
            <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
              <p className="mb-3 text-sm font-bold text-slate-700">Nouveau lien d’accès pour {user.displayName}</p>
              <LinkShare link={resetLink} name={user.displayName} login={user.login} />
            </div>
          ) : null}

          {panel === 'confirmReset' ? (
            <div className="mb-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900">
                Le mot de passe actuel de {user.displayName} cessera immédiatement de fonctionner, ainsi que les anciens liens. Un
                nouveau lien vous sera donné à lui transmettre.
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setPanel('none')} className={secondaryButtonClass}>
                  Annuler
                </button>
                <button type="button" onClick={() => void handleReset()} disabled={securityBusy} className={primaryButtonClass}>
                  Réinitialiser
                </button>
              </div>
            </div>
          ) : null}

          {panel === 'password' ? (
            <form onSubmit={handleSetPassword} className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Nouveau mot de passe</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder={`${MIN_PASSWORD_LENGTH} caractères minimum`}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Confirmation</label>
                  <input
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    autoComplete="new-password"
                    className={inputClass}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setPanel('none')} className={secondaryButtonClass}>
                  Annuler
                </button>
                <button type="submit" disabled={securityBusy} className={primaryButtonClass}>
                  Enregistrer le mot de passe
                </button>
              </div>
            </form>
          ) : null}

          <div className="mb-3 space-y-2">
            <ErrorBox message={securityError} />
            {securityNotice ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                {securityNotice}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {!isSelf && (
              <button type="button" onClick={() => setPanel('confirmReset')} disabled={securityBusy} className={secondaryButtonClass}>
                Réinitialiser le mot de passe
              </button>
            )}
            <button type="button" onClick={() => setPanel('password')} disabled={securityBusy} className={secondaryButtonClass}>
              Définir un mot de passe
            </button>
            {!isSelf && (
              <button type="button" onClick={() => void handleToggleActive()} disabled={securityBusy} className={secondaryButtonClass}>
                {status === 'disabled' ? 'Réactiver le compte' : 'Désactiver le compte'}
              </button>
            )}
            {currentUser.canManageAllUsers && !isSelf && status === 'active' && (
              <button
                type="button"
                onClick={() => void handleLoginAs()}
                disabled={securityBusy}
                className={`${secondaryButtonClass} border-violet-200 text-violet-700 hover:bg-violet-50`}
              >
                <LogIn className="h-4 w-4" />
                Se connecter en tant que
              </button>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminUsersPage: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<AdminAuthUser | null>(null);
  const [users, setUsers] = useState<AdminAuthUser[]>([]);
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [facilitators, setFacilitators] = useState<Facilitator[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [inviting, setInviting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newLink, setNewLink] = useState<{ name: string; login: string; link: string } | null>(null);
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const user = await authApi.me();
        setCurrentUser(user);
        if (user.canManageUsers) {
          const [userList, sessionList, facilitatorList] = await Promise.all([
            authApi.getUsers(),
            sessionsApi.getAllSessions(false, true),
            facilitatorsApi.getAllFacilitators().catch(() => [] as Facilitator[]),
          ]);
          setUsers(userList);
          setSessions(sessionList);
          setFacilitators(facilitatorList);
        }
      } catch (error) {
        setLoadError(getApiErrorMessage(error, 'Erreur lors du chargement des utilisateurs'));
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => [user.displayName, user.email, user.login].some((value) => value?.toLowerCase().includes(q)));
  }, [users, query]);

  const upsertUser = (updated: AdminAuthUser) =>
    setUsers((prev) => (prev.some((u) => u.id === updated.id) ? prev.map((u) => (u.id === updated.id ? updated : u)) : [...prev, updated]));

  const handleNewLink = async (user: AdminAuthUser) => {
    setRowError('');
    setRowBusyId(user.id);
    try {
      const { user: updated, setupToken } = await authApi.resetLink(user.id);
      upsertUser(updated);
      setNewLink({ name: user.displayName, login: updated.login, link: buildSetupLink(setupToken) });
    } catch (error) {
      setRowError(getApiErrorMessage(error, 'Impossible de générer un nouveau lien'));
    } finally {
      setRowBusyId(null);
    }
  };

  const closeInvite = React.useCallback(() => setInviting(false), []);
  const closeEdit = React.useCallback(() => setEditingId(null), []);
  const closeNewLink = React.useCallback(() => setNewLink(null), []);

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!currentUser?.canManageUsers) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-4 shadow-sm">
        <p className="text-center text-slate-500">{loadError || 'Accès réservé aux comptes autorisés à gérer les utilisateurs.'}</p>
      </div>
    );
  }

  const editingUser = users.find((u) => u.id === editingId) ?? null;

  return (
    <div className="font-sans text-slate-900">
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="border-l-4 border-violet-500 pl-3 text-xl font-black tracking-tight text-slate-900">Utilisateurs</h1>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher…"
                className={`${inputClass} w-56 pl-9`}
              />
            </div>
            <button type="button" onClick={() => setInviting(true)} className={primaryButtonClass}>
              <UserPlus className="h-4 w-4" />
              Inviter un utilisateur
            </button>
          </div>
        </div>

        {(loadError || rowError) && (
          <div className="mb-3">
            <ErrorBox message={loadError || rowError} />
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((user) => {
                const status = getStatus(user);
                return (
                  <tr key={user.id} className="group hover:bg-violet-50/40">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setEditingId(user.id)} className="flex min-w-0 items-center gap-3 text-left">
                        <Avatar user={user} />
                        <span className="min-w-0">
                          <span className="block truncate font-bold text-slate-900">
                            {user.displayName}
                            {user.id === currentUser.id && <span className="ml-2 text-xs font-medium text-slate-400">(vous)</span>}
                          </span>
                          {user.hasEmail === false ? (
                            <span className="block truncate text-xs text-slate-500">
                              {user.login} · <span className="font-semibold text-amber-600">Email à compléter</span>
                            </span>
                          ) : (
                            <span className="block truncate text-xs text-slate-500">
                              {user.email}
                              {user.login && user.login !== user.email && <span className="text-slate-400"> · {user.login}</span>}
                            </span>
                          )}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="whitespace-nowrap rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-bold text-violet-700">
                        {ROLE_LABEL[user.role] ?? user.role}
                      </span>
                      {user.parentAdminUserId && (
                        <span className="mt-1 block whitespace-nowrap text-[11px] text-slate-400">
                          Géré par {managerLabel(users, user.parentAdminUserId)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {status === 'pending' && user.id !== currentUser.id && (
                          <button
                            type="button"
                            onClick={() => void handleNewLink(user)}
                            disabled={rowBusyId === user.id}
                            className={rowActionClass}
                          >
                            <Link2 className="h-3.5 w-3.5" />
                            Nouveau lien
                          </button>
                        )}
                        <button type="button" onClick={() => setEditingId(user.id)} className={rowActionClass}>
                          <Pencil className="h-3.5 w-3.5" />
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-400">
                    {query ? 'Aucun utilisateur ne correspond à la recherche.' : 'Aucun utilisateur pour le moment.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {inviting && (
        <InviteModal
          currentUser={currentUser}
          existingUsers={users}
          sessions={sessions}
          facilitators={facilitators}
          onClose={closeInvite}
          onCreated={upsertUser}
        />
      )}

      {editingUser && (
        <EditModal
          key={editingUser.id}
          user={editingUser}
          currentUser={currentUser}
          users={users}
          sessions={sessions}
          facilitators={facilitators}
          onClose={closeEdit}
          onUpdated={upsertUser}
        />
      )}

      {newLink && (
        <Modal title={`Nouveau lien pour ${newLink.name}`} onClose={closeNewLink}>
          <LinkShare link={newLink.link} name={newLink.name} login={newLink.login} />
        </Modal>
      )}
    </div>
  );
};
